import { resolve } from 'redux-optimist-promise';
import logger from 'Logger/logger';
import { remove } from 'lodash';
import moment from 'moment';
import type { MemberHousehold } from 'Views/booking/receipt/home-details/HomeDetailsWrapper.utils';
import 'Views/booking/receipt/home-details/HomeDetailsWrapper.utils';
import type {
  ChildAttendeeWithInfosType,
  AdultAttendeeWithInfosType,
} from 'Store/booking/booking.selector';
import 'Store/booking/booking.selector';
import {
  FETCH_PROPOSALS,
  FETCH_PROPOSALS_BY_ID,
  PREPARE_BE,
  EDIT_HOUSEHOLDS,
  UPDATE_ATTENDEES,
} from 'Store/booking/proposals/constants';
import { SET_ATTENDEES } from 'Store/trip-information/adults/constants';
import { SET_CUSTOMER_SEARCH_RESULT } from 'Store/booking/customer-search/customerSearch.reducer';
import { createId } from 'Components/widgets/contact-info/ContactInfoUtils';
import { formatHouseholds } from './attendees.selector';
type State = {
  adultsAttendees: Array<AdultAttendeeWithInfosType>;
  kidsAttendees: Array<ChildAttendeeWithInfosType>;
  isMultiHouseHold: boolean;
  attendeesByHouseHolds: Array<MemberHousehold>;
  numberOfHouseHolds: number;
};
type ActionType = {
  type: string;
  payload: {
    selectedProposal: Proposal;
    proposal: {
      households: Array<any>;
      attendees: any;
    };
    proposals: {
      proposals: Array<Proposal>;
    };
    kidsAttendees?: Array<any>;
    adultsAttendees?: Array<any>;
    households?: any;
    data: {
      adults: Array<any>;
      children: Array<any>;
    };
    searchedHouseholdIndex: number;
    attendees?: Array<any>;
  };
};
const initialState: State = {
  isMultiHouseHold: false,
  attendeesByHouseHolds: [],
  numberOfHouseHolds: 0,
  kidsAttendees: [],
  adultsAttendees: [],
};

function shuffleKidsThatWillBeAdult(
  adults: Array<AdultAttendeeWithInfosType> | undefined,
  children: Array<ChildAttendeeWithInfosType> | undefined,
  arrivalDate?: string,
): {
  shuffledAdults: Array<AdultAttendeeWithInfosType>;
  shuffledChildren: Array<ChildAttendeeWithInfosType>;
} {
  if (arrivalDate) {
    const otherChildren = children ? [...children] : [];
    const kidsThatWillBeAdults = remove(
      otherChildren,
      (kid) => moment(arrivalDate).diff(moment(kid.birthdate || kid?.dateOfBirth), 'years') >= 18,
    );
    const actualAdultsAttendees = adults?.concat(
      kidsThatWillBeAdults.map((newAdult) => ({ ...newAdult, address: {} })) || [],
    );
    return {
      shuffledAdults: actualAdultsAttendees || [],
      shuffledChildren: otherChildren,
    };
  }

  return {
    shuffledAdults: adults || [],
    shuffledChildren: children || [],
  };
}

export function attendeesReducer(
  state: State = initialState,
  action: ActionType,
): State | null | undefined {
  switch (action.type) {
    case resolve(FETCH_PROPOSALS):
    case SET_ATTENDEES:
    case PREPARE_BE:
    case resolve(FETCH_PROPOSALS_BY_ID): {
      const { adultsAttendees, kidsAttendees, selectedProposal, proposals } = action.payload;
      const defaultProposal = selectedProposal || proposals?.proposals?.[0];
      const { shuffledAdults, shuffledChildren } = shuffleKidsThatWillBeAdult(
        state.adultsAttendees.concat(adultsAttendees || []),
        state.kidsAttendees.concat(kidsAttendees || []),
        defaultProposal?.arrivalDate,
      );
      const attendeesByHouseHolds = formatHouseholds(defaultProposal);
      return {
        ...state,
        adultsAttendees: shuffledAdults,
        kidsAttendees: shuffledChildren,
        isMultiHouseHold: attendeesByHouseHolds.length > 1,
        attendeesByHouseHolds,
        numberOfHouseHolds: attendeesByHouseHolds.length,
      };
    }

    case UPDATE_ATTENDEES: {
      const attendeesByHouseHolds = formatHouseholds(
        action?.payload?.proposal as Proposal,
        action?.payload?.attendees,
      );
      return {
        ...state,
        isMultiHouseHold: attendeesByHouseHolds.length > 1,
        attendeesByHouseHolds,
        numberOfHouseHolds: attendeesByHouseHolds.length,
      };
    }

    case EDIT_HOUSEHOLDS:
      const attendeesByHouseHolds = action.payload?.households || state.attendeesByHouseHolds;
      return { ...state, attendeesByHouseHolds, numberOfHouseHolds: attendeesByHouseHolds.length };

    case SET_CUSTOMER_SEARCH_RESULT:
      try {
        if (!action.payload.data) {
          return { ...state };
        }

        const {
          data: { adults: searchedAdults, children: searchedChildren },
          searchedHouseholdIndex = 0,
        } = action.payload;
        const currentHouseholds = [...state.attendeesByHouseHolds];
        const currentHousehold = currentHouseholds[searchedHouseholdIndex];
        currentHouseholds[searchedHouseholdIndex] = {
          ...currentHousehold,
          adults: currentHousehold.adults.map((adult, index) => ({
            ...adult,
            ...searchedAdults[index],
            customerId: searchedAdults[index]?.id,
            neolid: '',
            id: adult.id || createId(index, 'A'),
          })),
          children: currentHousehold.children.map((child, index) => ({
            ...child,
            ...searchedChildren[index],
            customerId: searchedChildren[index]?.id,
            neolid: '',
            id: child.id || createId(index, 'A'),
          })),
        };
        return {
          ...state,
          attendeesByHouseHolds: currentHouseholds,
          numberOfHouseHolds: currentHouseholds.length,
        };
      } catch (error) {
        logger.error(error);
        return { ...state };
      }

    default:
      return state;
  }
}
